import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleBogosDrawerOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleBogosDrawerOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Chrome-less home_mobile hides #header-group (where this drawer lives).
   * Opening would scroll-lock the body with an invisible modal — skip it.
   */
  showDialog() {
    if (document.documentElement.classList.contains('home-mobile')) return;
    super.showDialog();
  }

  /**
   * Handles cart add events - announces the count when the drawer is open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // Opening is driven from module scope instead, so that a section morph which
    // rebuilds this element cannot cancel it. See observeAutoOpen.
    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Re-dispatch the cart payload so BOGOS can re-render dynamic mounts.
   * @param {CustomEvent<{ resource?: object }>} event
   */
  #handleCartUpdate = (event) => {
    this.#notifyBogos(event.detail.resource);
  };

  #handleBogosDrawerOpen = async () => {
    const mount = this.refs.dialog?.querySelector('.bogos-gift-slider-view');
    if (!mount) return;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!response.ok) return;

      const cart = await response.json();
      if (!cart?.item_count) return;
      this.#notifyBogos(cart);
    } catch {
      // Silently ignore BOGOS refresh failures.
    }
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  #notifyBogos(cart) {
    if (!cart) return;

    document.dispatchEvent(new CustomEvent('bogos:gifts', { detail: cart }));
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Keep the checkout/view-bag summary pinned to the bottom of the drawer while
    // .cart-drawer__items scrolls underneath it.
    dialog.setAttribute('cart-summary-sticky', 'true');
  }
}

/**
 * Opens the drawer on a cart addition, from module scope rather than from an instance.
 *
 * The cart:update dispatch that should open the drawer is also what re-renders the
 * header section, and cart-items-component morphs it synchronously from an earlier
 * listener on the same event. On the first add to an empty cart the drawer's markup
 * changes shape, so that morph rebuilds <cart-drawer-component>: the old instance's
 * listener is removed before the dispatch reaches it, and the replacement's listener
 * is registered mid-dispatch, which the DOM spec skips. Neither one opens the drawer,
 * which is why only the first add after a page load went unnoticed.
 *
 * Watching here outlives those rebuilds. Waiting for the next frame lets the morph
 * settle so the drawer opens with its new contents, and showDialog is a no-op once the
 * dialog is open, so overlapping calls are harmless.
 */
function observeAutoOpen() {
  document.addEventListener(CartAddEvent.eventName, () => {
    let framesLeft = 3;

    const openWhenRendered = () => {
      const drawer = document.querySelector('cart-drawer-component');

      if (drawer instanceof CartDrawerComponent) {
        if (drawer.hasAttribute('auto-open')) drawer.showDialog();
        return;
      }

      // A rebuilt element can still be waiting to upgrade on the first frame.
      if (framesLeft-- > 0) requestAnimationFrame(openWhenRendered);
    };

    requestAnimationFrame(openWhenRendered);
  });
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
  observeAutoOpen();
}